/**
 * Decision Log screen — the workspace's memorialized decisions, newest
 * first. C-Mode: search, one kind filter, click through to the record.
 *
 * Record a decision (roadmap S-11, ruling R16): a short form on the one
 * governed write (`createDecision` → `record_decision`, migration 765), which
 * any member of the workspace may make. The full Decision Log keeps the long
 * form (class, tags, linked records, superseding).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDecisionsStore } from '@/stores/prioritization/decisionsStore';
import {
  DECISION_CLASS_LABELS,
  type Decision,
  type DecisionKind,
} from '@/types/prioritization/decisions';
import { getAppDefinition } from '../registry';
import { AppShell, ScreenHeading } from '../components/AppShell';
import { AppCard, EmptyState, Eyebrow, PrimaryButton, SecondaryButton, StatusChip } from '../components/primitives';
import type { DecisionImpact } from '@/types/prioritization/decisions';

const IMPACTS: { value: DecisionImpact; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

/** The short form: what was decided, why, and how much it matters. */
function RecordDecisionCard({ onRecorded }: { onRecorded: (id: string) => void }) {
  const createDecision = useDecisionsStore((s) => s.createDecision);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [why, setWhy] = useState('');
  const [impact, setImpact] = useState<DecisionImpact>('medium');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) {
    return (
      <div className="mb-4">
        <PrimaryButton onClick={() => setOpen(true)} data-testid="record-decision-open">
          Record a decision
        </PrimaryButton>
      </div>
    );
  }

  const save = async () => {
    if (!title.trim() || saving) return;
    setSaving(true);
    setError(null);
    const created = await createDecision({ title: title.trim(), body: why.trim() || undefined, impact });
    setSaving(false);
    if (!created) {
      setError(useDecisionsStore.getState().lastError ?? 'The decision was not saved. Try again.');
      return;
    }
    setOpen(false);
    setTitle('');
    setWhy('');
    setImpact('medium');
    onRecorded(created.id);
  };

  const fieldClass =
    'w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]';

  return (
    <AppCard>
      <Eyebrow>Record a decision</Eyebrow>
      <div className="mt-3 space-y-3" data-testid="record-decision-form">
        <label className="block">
          <span className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">What was decided</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. We ship the partner API before onboarding."
            className={`mt-1 ${fieldClass}`}
          />
        </label>
        <label className="block">
          <span className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">Why</span>
          <textarea
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            rows={3}
            placeholder="The reasons, so the record says why as well as what."
            className={`mt-1 ${fieldClass}`}
          />
        </label>
        <div>
          <span className="font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">How much it matters</span>
          <div className="mt-1 flex gap-1.5">
            {IMPACTS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setImpact(option.value)}
                aria-pressed={impact === option.value}
                className={`rounded-full px-3 py-1.5 font-body text-xs ${
                  impact === option.value
                    ? 'bg-primary-600 text-white'
                    : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        {error ? <p role="alert" className="font-body text-sm text-[var(--coral)]">{error}</p> : null}
        <div className="flex gap-2">
          <PrimaryButton disabled={!title.trim() || saving} onClick={() => void save()}>
            {saving ? 'Recording…' : 'Record it'}
          </PrimaryButton>
          <SecondaryButton onClick={() => setOpen(false)}>Cancel</SecondaryButton>
        </div>
        <p className="font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
          It goes on the record under your name, dated today.
        </p>
      </div>
    </AppCard>
  );
}

type KindFilter = 'all' | DecisionKind;

const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'auto', label: 'Recorded by the system' },
  { value: 'manual', label: 'Entered by a person' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function matchesQuery(d: Decision, q: string): boolean {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    d.title.toLowerCase().includes(needle) ||
    (d.body ?? '').toLowerCase().includes(needle) ||
    (d.decidedByName ?? '').toLowerCase().includes(needle)
  );
}

export default function DecisionLogScreen() {
  const app = getAppDefinition('decisions')!;
  const navigate = useNavigate();
  const { decisions, syncState, fetchDecisions } = useDecisionsStore();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<KindFilter>('all');

  useEffect(() => {
    void fetchDecisions();
  }, [fetchDecisions]);

  const visible = useMemo(
    () =>
      decisions.filter(
        (d) => (kind === 'all' || d.decisionKind === kind) && matchesQuery(d, query),
      ),
    [decisions, kind, query],
  );

  const loading = syncState.isLoading && decisions.length === 0;

  return (
    <AppShell app={app}>
      <ScreenHeading
        title="The record"
        subtitle="Every consequential decision, memorialized — who, when, why. Each one is a link you can send."
      />

      <RecordDecisionCard onRecorded={(id) => navigate(`/apps/decisions/${id}`)} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search decisions…"
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-500 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-pri)]"
        />
        <div className="flex shrink-0 gap-1.5">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setKind(f.value)}
              className={`rounded-full px-3 py-1.5 font-body text-xs ${
                kind === f.value
                  ? 'bg-primary-600 text-white'
                  : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <p className="font-body text-sm text-gray-500 dark:text-[var(--text-sec)]">
          Loading the record…
        </p>
      ) : visible.length === 0 ? (
        <EmptyState
          title={decisions.length === 0 ? 'Nothing on the record yet' : 'No decisions match'}
          hint={
            decisions.length === 0
              ? 'Decisions are recorded automatically when a session publishes, a priority list is approved, or a published ranking is changed through the gate — and you can record one here.'
              : 'Try a different search or filter.'
          }
        />
      ) : (
        <div className="space-y-2">
          <Eyebrow>
            {visible.length} decision{visible.length === 1 ? '' : 's'} on the record
          </Eyebrow>
          {visible.map((d) => (
            <AppCard key={d.id} onClick={() => navigate(`/apps/decisions/${d.id}`)}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
                    {d.title}
                  </p>
                  <p className="mt-0.5 font-body text-xs text-gray-500 dark:text-[var(--text-sec)]">
                    {d.decidedByName ?? (d.decisionKind === 'auto' ? 'Recorded by the system' : 'Unattributed')}
                    {' · '}
                    {formatDate(d.decidedAt)}
                    {d.decisionClass ? ` · ${DECISION_CLASS_LABELS[d.decisionClass]}` : ''}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {d.impact === 'high' ? <StatusChip label="High impact" tone="mustard" /> : null}
                  {d.status === 'superseded' ? <StatusChip label="Superseded" tone="neutral" /> : null}
                  {d.status === 'reversed' ? <StatusChip label="Reversed" tone="coral" /> : null}
                </div>
              </div>
            </AppCard>
          ))}
        </div>
      )}

      <p className="mt-6 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
        Need the map view, rich filters, or to link a decision to records?{' '}
        <Link
          to="/prioritization/decisions"
          className="text-primary-700 underline-offset-2 hover:underline dark:text-[var(--text-sec)]"
        >
          Open the full Decision Log
        </Link>
        .
      </p>
    </AppShell>
  );
}
